import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { QueryClient } from '@tanstack/react-query';

export interface Site {
  id: number;
  name: string;
  url: string;
  sitemapUrl?: string;
  organizationId: number; // tenantId reference
  createdAt: string;
  updatedAt: string;
  propertyType?: 'DOMAIN' | 'URL_PREFIX';
  timezone?: string; // Site timezone
  isActive?: boolean; // Active status
  configuration?: Record<string, unknown>; // Additional configuration
  mode?: string; // EARLY_STAGE, PERFORMANCE, SETUP
  businessType?: string;
  customerEmail?: string; // Customer contact email
  customerName?: string; // Customer name
  customerId?: string; // Customer ID
  gscPropertyId?: string;
  bingId?: string;
  ga4PropertyId?: string;
  pinterestId?: string;
  agencyCode?: string; // Agency code — links this site to an agency for multi-tenant access
}

interface SiteStore {
  // State
  selectedSite: Site | null;
  sites: Site[];
  isLoading: boolean;
  error: string | null;
  
  // Active page (cleared on site switch)
  selectedPageId: number | null;

  // Actions
  setSite: (site: Site, queryClient?: QueryClient) => void;
  clearSite: () => void;
  setSelectedPage: (pageId: number | null) => void;
  setSites: (sites: Site[]) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useSiteStore = create<SiteStore>()(
  persist(
    (set, get) => ({
      selectedSite: null,
      sites: [],
      isLoading: false,
      error: null,
      selectedPageId: null,

      setSite: (site, queryClient) => {
        const previousSite = get().selectedSite;
        
        // Check if actually switching sites
        if (previousSite?.id !== site.id) {
          console.log(`🔄 Switching site: ${previousSite?.name || 'None'} → ${site.name}`);
          
          // Clear selected page when switching sites
          set({ selectedSite: site, selectedPageId: null });
          
          // Invalidate site-specific queries but preserve cached data
          if (queryClient) {
            console.log('🔄 Switching to new site context, preserving cached data...');
            
            // Smart cache management: Keep site-specific caches (health, pages)
            // Only invalidate queries that don't have siteId in their key
            queryClient.invalidateQueries({
              predicate: (query: any) => {
                const key = query.queryKey;
                // Keep: sites list, user auth, health per site, pages per site
                if (
                  key[0] === 'sites' || 
                  key[0] === 'user' || 
                  key[0] === 'auth' ||
                  key[0] === 'dataSourceHealth' || // Keep health cache per site
                  key[0] === 'pages-for-site' // Keep pages cache per site
                ) {
                  return false; // Don't invalidate
                }
                // Invalidate everything else
                return true;
              }
            });
            
            console.log('✅ Site context switched, per-site caches preserved');
          }
          
          // Trigger event for other components
          window.dispatchEvent(new CustomEvent('site-changed', { 
            detail: { previousSite, newSite: site } 
          }));
        } else {
          // Same site, just update reference
          set({ selectedSite: site });
        }
      },

      clearSite: () => {
        set({ selectedSite: null, selectedPageId: null });
      },

      setSelectedPage: (pageId) => {
        set({ selectedPageId: pageId });
      },

      setSites: (sites) => {
        const sortedSites = [...sites].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
        );

        set({ sites: sortedSites, error: null });

        const currentSelected = get().selectedSite;
        const hasCurrentSelection =
          !!currentSelected && sortedSites.some((site) => site.id === currentSelected.id);

        // Auto-select default site as first alphabetical when there is no valid current selection.
        if (sortedSites.length > 0 && !hasCurrentSelection) {
          console.log('📍 Auto-selecting default site (alphabetical):', sortedSites[0].name);
          get().setSite(sortedSites[0]);
        }
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setError: (error) => {
        set({ error, isLoading: false });
      },
    }),
    {
      name: 'site-context-storage',
      partialize: (state) => ({
        selectedSite: state.selectedSite,
        selectedPageId: state.selectedPageId,
      }),
    }
  )
);
